import type { ContractClientRelation } from "@closer/shared";
import { Button, Card, Input, Spinner, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * SÖZLEŞMENİN MÜVEKKİLİ (CLOS-824).
 *
 * ### ⚠ Neden sözleşme sayfasında
 *
 * Bağ önceden yalnız müvekkil sayfasından kurulabiliyordu: kullanıcı sözleşmeyi açmışken
 * müvekkil listesine gidip sözleşmeyi aramak zorundaydı. Sözleşmeden bakan biri için
 * ilişki YOK gibi görünüyordu.
 *
 * ### ⚠ AYNI UÇ, ikinci bir bağlama mantığı yok
 *
 * Ekleme ve kaldırma `/api/clients/:id/contracts` uçlarına gidiyor — müvekkil sayfasının
 * kullandığı uçların ta kendisi. İkinci bir yol açsaydık iki kural kümesi zamanla ayrışır
 * ve "hangisi doğru" sorusu ortaya çıkardı.
 *
 * ### ⚠ Bağ TARAF DEĞİL
 *
 * Hemen üstteki "Taraflar" bölümüyle karıştırılmamalı ve metin bunu söylüyor: bir müvekkil,
 * tarafı olmadığı bir sözleşmenin portföyünde bulunabilir. Bağ kurmak kimseye sözleşme
 * erişimi de vermiyor (CLOS-819 kuralı, sunucuda).
 */
export function ContractClientSection({
  contractId,
  arsivde,
}: {
  contractId: string;
  arsivde: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId ?? undefined;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [arama, setArama] = useState("");

  const yonetebilir = !arsivde && can("client.contract_link");

  const bagliMuvekkiller = useQuery({
    queryKey: tenantKey(activeTenantId ?? "", "contract-clients", contractId),
    queryFn: () =>
      api<ContractClientRelation[]>(`/api/contracts/${contractId}/clients`, { tenantId }).then(
        (r) => r.data,
      ),
    enabled: Boolean(activeTenantId),
  });

  const adaylar = useQuery({
    queryKey: tenantKey(activeTenantId ?? "", "contract-clients-adaylar", arama),
    queryFn: () =>
      api<{ items: Array<{ id: string; displayName: string }> }>(
        `/api/clients?q=${encodeURIComponent(arama)}&limit=5`,
        { tenantId },
      ).then((r) => r.data.items),
    enabled: yonetebilir && arama.trim().length > 0,
  });

  const tazele = async () => {
    await queryClient.invalidateQueries({
      queryKey: tenantKey(activeTenantId ?? "", "contract-clients", contractId),
    });
  };

  const bagla = useMutation({
    mutationFn: (clientId: string) =>
      api(`/api/clients/${clientId}/contracts`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ contractId }),
      }),
    onSuccess: async () => {
      setArama("");
      await tazele();
      toast.success("Sözleşme müvekkile bağlandı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Müvekkil bağlanamadı"),
  });

  const kaldir = useMutation({
    mutationFn: (clientId: string) =>
      api(`/api/clients/${clientId}/contracts/${contractId}`, { method: "DELETE", tenantId }),
    onSuccess: async () => {
      await tazele();
      toast.success("Müvekkil bağı kaldırıldı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Bağ kaldırılamadı"),
  });

  /** ⚠ Müvekkil yüzeyi kapalı bağlamda bölüm HİÇ çizilmiyor: uç da orada 403 dönüyor. */
  if (!can("client.view")) return null;

  return (
    <Card title="Müvekkil">
      <div className="flex flex-col gap-3">
        {bagliMuvekkiller.isPending ? (
          <Spinner />
        ) : bagliMuvekkiller.isError ? (
          /* ⚠ HATA "yok" DEĞİL: boş liste göstermek, bağı olan sözleşmeyi bağsız gösterirdi. */
          <p className="text-sm text-danger">Bağlı müvekkil bilgisi getirilemedi.</p>
        ) : bagliMuvekkiller.data!.length === 0 ? (
          <p className="text-sm text-fg-muted">Bu sözleşme henüz bir müvekkile bağlanmamış.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {bagliMuvekkiller.data!.map((bag) => (
              <li key={bag.id} className="flex items-center gap-2 text-sm text-fg">
                <span className="font-medium">{bag.clientDisplayName}</span>
                {yonetebilir ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={kaldir.isPending}
                    onClick={() => kaldir.mutate(bag.clientId)}
                  >
                    Bağı kaldır
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {yonetebilir ? (
          <div className="flex flex-col gap-2">
            <Input
              aria-label="Bağlanacak müvekkili ara"
              placeholder="Müvekkil adı"
              value={arama}
              onChange={(e) => setArama(e.target.value)}
            />
            {adaylar.data?.length ? (
              <ul className="flex flex-col gap-1">
                {adaylar.data.map((aday) => (
                  <li key={aday.id}>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      loading={bagla.isPending}
                      onClick={() => bagla.mutate(aday.id)}
                    >
                      {aday.displayName}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <p className="text-xs text-fg-soft">
              Müvekkil bağı sözleşmeye taraf eklemez ve kimseye sözleşme erişimi vermez.
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
